
import { useState } from "react";
import { useUpload } from "@/hooks/use-upload";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";

export const jobApplicationSchema = z.object({
  fullName: z.string().min(2, "Full name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().min(10, "Please enter a valid phone number"),
  preferredCounty: z.string().min(1, "Please select a preferred county"),
  nursingHomeSlug: z.string().optional(),
  hasExperience: z.enum(["yes", "no"]),
  legalRightToWork: z.enum(["yes", "no"], {
    required_error: "Please indicate if you have the legal right to work in Ireland",
  }),
  message: z.string().min(10, "Please provide a brief message or cover letter"),
  cvFile: z.any().refine((file) => file && file.length > 0, {
    message: "CV is required",
  }),
  consent: z.boolean().refine((val) => val === true, {
    message: "You must agree to the privacy policy",
  }),
});

export type JobApplicationFormData = z.infer<typeof jobApplicationSchema>;

export function useJobApplication() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const { uploadImage, isUploading } = useUpload();
  const { toast } = useToast();

  const submitJobApplication = async (data: JobApplicationFormData): Promise<boolean> => {
    try {
      setIsSubmitting(true);
      setSubmissionError(null);
      setSubmissionSuccess(false);
      
      // Step 1: Upload CV file
      const cvFile = data.cvFile && data.cvFile.length > 0 ? data.cvFile[0] : null;
      if (!cvFile) {
        throw new Error("CV file is required");
      }
      
      console.log("Uploading CV file...");
      const uploadResult = await uploadImage(cvFile, "public");
      
      if (!uploadResult) {
        throw new Error("Failed to upload CV file");
      }
      
      console.log("CV uploaded successfully:", uploadResult);
      
      // Step 2: Save application data to database
      const { error: insertError } = await supabase
        .from('job_applications')
        .insert({
          full_name: data.fullName,
          email: data.email,
          phone: data.phone,
          preferred_county: data.preferredCounty,
          nursing_home_slug: data.nursingHomeSlug || null,
          has_experience: data.hasExperience === "yes",
          legal_right_to_work: data.legalRightToWork === "yes",
          message: data.message,
          cv_file_id: uploadResult.id,
          consent: data.consent
        });
      
      if (insertError) {
        console.error("Database error:", insertError);
        throw new Error(`Failed to save application data: ${insertError.message}`);
      }
      
      // Step 3: Process application and send notifications
      console.log("Sending application to processing function...");
      const { data: processingResult, error: processingError } = await supabase.functions.invoke(
        'process-job-application',
        {
          body: JSON.stringify({
            application: data,
            cvFileInfo: uploadResult
          })
        }
      );
      
      if (processingError) {
        console.error("Processing error:", processingError);
        throw new Error(`Error processing application: ${processingError.message}`);
      }
      
      console.log("Application processed:", processingResult);
      
      setSubmissionSuccess(true);
      toast({
        title: "Application submitted successfully",
        description: "Thank you for your interest! We will contact you soon.",
      });
      
      return true;
    } catch (error: any) {
      console.error("Application submission error:", error);
      setSubmissionError(error.message || "Failed to submit application");
      
      toast({
        title: "Application submission failed",
        description: error.message || "Please try again later",
        variant: "destructive",
      });
      
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return {
    submitJobApplication,
    isSubmitting: isSubmitting || isUploading,
    submissionError,
    submissionSuccess,
    resetSubmission: () => {
      setSubmissionError(null);
      setSubmissionSuccess(false);
    }
  };
}
